import { Controller } from "@hotwired/stimulus"

// Wraps a Trix editor with the three behaviours the stylesheet can't do alone.
//
//   1. **Overflow.** The core six controls stay visible; the rest collapse
//      behind a ⋯ toggle. Nothing is removed — same eleven buttons, same
//      keyboard shortcuts — so muscle memory is unaffected.
//   2. **Attachments off.** A dropped image gets a one-line notice pointing at
//      the rulebook uploader that already sits above the editor, rather than a
//      second, half-styled attachment story in the same panel.
//   3. **Accessible labels**, since the buttons are icon-only once themed.
// Keyed by Trix's own names, which aren't always the obvious ones: the link
// button carries data-trix-attribute="href", and nesting uses actions rather
// than attributes.
const LABELS = {
  bold: "Bold",
  italic: "Italic",
  href: "Link",
  strike: "Strikethrough",
  quote: "Quote",
  code: "Code",
  bullet: "Bulleted list",
  number: "Numbered list",
  heading1: "Heading",
  decreaseNestingLevel: "Outdent",
  increaseNestingLevel: "Indent",
  undo: "Undo",
  redo: "Redo",
}

export default class extends Controller {
  static targets = ["notice"]
  static values = {
    overflow: { type: Boolean, default: true },
    // Off by default: the drawer's editor has an autosave line under it and
    // does not need a second readout.
    counter: { type: Boolean, default: false },
  }

  connect() {
    this.element.addEventListener("trix-initialize", this.decorate)
    this.element.addEventListener("trix-file-accept", this.rejectFile)
    if (this.counterValue)
      this.element.addEventListener("trix-change", this.countWords)
    // trix-initialize may already have fired before this controller connected.
    if (this.element.querySelector("trix-toolbar")) this.decorate()
  }

  disconnect() {
    this.element.removeEventListener("trix-initialize", this.decorate)
    this.element.removeEventListener("trix-file-accept", this.rejectFile)
    this.element.removeEventListener("trix-change", this.countWords)
  }

  decorate = () => {
    const toolbar = this.element.querySelector("trix-toolbar")
    if (!toolbar || toolbar.dataset.decorated === "true") return
    toolbar.dataset.decorated = "true"

    toolbar
      .querySelectorAll("[data-trix-attribute], [data-trix-action]")
      .forEach((button) => {
        const key = button.dataset.trixAttribute || button.dataset.trixAction
        const label = LABELS[key]
        if (label && !button.getAttribute("aria-label"))
          button.setAttribute("aria-label", label)
        if (label) button.setAttribute("title", label)
      })

    if (!this.overflowValue) return this.addCounter(toolbar)

    this.element.dataset.overflow = "collapsed"
    const toggle = document.createElement("button")
    toggle.type = "button"
    toggle.className = "apx-trix__overflow"
    toggle.setAttribute("aria-label", "More formatting")
    toggle.setAttribute("aria-expanded", "false")
    toggle.title = "More formatting"
    toggle.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" ' +
      'style="width:16px;height:16px" aria-hidden="true"><path stroke-linecap="round" ' +
      'stroke-linejoin="round" d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm6 0a.75.75 0 ' +
      '1 1-1.5 0 .75.75 0 0 1 1.5 0Zm6 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"/></svg>'
    toggle.addEventListener("click", () => {
      const collapsed = this.element.dataset.overflow === "collapsed"
      this.element.dataset.overflow = collapsed ? "expanded" : "collapsed"
      toggle.setAttribute("aria-expanded", collapsed ? "true" : "false")
    })
    toolbar.querySelector(".trix-button-row")?.appendChild(toggle)
    this.addCounter(toolbar)
  }

  addCounter(toolbar) {
    if (!this.counterValue || this.counter) return
    this.counter = document.createElement("span")
    this.counter.className = "apx-trix__counter"
    toolbar.querySelector(".trix-button-row")?.appendChild(this.counter)
    this.countWords()
  }

  // Words, not characters: the field asks for "a few lines", and a character
  // count reads like a limit nobody set.
  countWords = () => {
    if (!this.counter) return
    const editor = this.element.querySelector("trix-editor")
    const text = editor?.editor?.getDocument()?.toString().trim() || ""
    const n = text ? text.split(/\s+/).length : 0
    this.counter.textContent = n ? `${n} ${n === 1 ? "word" : "words"}` : ""
  }

  // Attachments are off by product decision, so refuse the file and say where
  // a rulebook actually goes.
  rejectFile = (event) => {
    event.preventDefault()
    if (!this.hasNoticeTarget) return
    this.noticeTarget.textContent =
      "Images aren't supported here. Attach a rulebook PDF above instead."
    this.noticeTarget.classList.remove("hidden")
    clearTimeout(this.noticeTimer)
    this.noticeTimer = setTimeout(
      () => this.noticeTarget.classList.add("hidden"),
      6000,
    )
  }
}
